/// <amd-module name="CodeCoverage/CodeCoverageCiTab/Components/CodeCoverageCiTabBar" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, IFilter } from "VSSUI/Utilities/Filter";

import { CodeCoverageCiChangedTab } from "CodeCoverage/CodeCoverageCiChangedTab/ChangedTab";
import * as Resources from "CodeCoverage/CodeCoverageCiTab/Resources";
import { CoverageChangeSummaryResponse } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";
import { TelemetryEvents } from "CodeCoverage/ViewCommon/Constants";
import { publish } from "CodeCoverage/ViewCommon/Helpers";
import { RunViewArtifact } from "CodeCoverage/ViewCommon/RunViewArtifact";

export const enum TabType {
    Changed = "Changed",
    All = "All"
}

export interface ICodeCoverageCiTabProps {
    summaryResponse: CoverageChangeSummaryResponse;
    runViewArtifact: RunViewArtifact;
}

export class CodeCoverageCiTabBar extends VssComponent<ICodeCoverageCiTabProps, {}> {
    private selectedTabId: ObservableValue<TabType>;
    private changedTabFilter: IFilter;
    private allTabFilter: IFilter;

    constructor(props: ICodeCoverageCiTabProps) {
        super(props);
        this.selectedTabId = new ObservableValue(TabType.Changed);
        this.changedTabFilter = new Filter();
        this.allTabFilter = new Filter();
    }

    public render() {
        return (
            <div className="codecoverage-ci-tabbar-container flex-grow depth-4">
                <TabBar
                    className="codecoverage-ci-tabbar"
                    onSelectedTabChanged={this.onSelectedTabChanged}
                    selectedTabId={this.selectedTabId}
                    tabSize={TabSize.Compact}
                    renderAdditionalContent={this.onRenderFilterBar}
                >
                    <Tab className="margin-bottom-16 margin-horizontal-16" name={Resources.ChangedTab} id={TabType.Changed} />
                    {isFeatureFlagEnabled(this.context.pageContext, "WebAccess.TestManagement.CodeCoverage.AllTab", false) && (
                        <Tab className="margin-bottom-16 margin-horizontal-16" name={Resources.AllTab} id={TabType.All} />
                    )}
                </TabBar>
                <Observer selectedTabId={this.selectedTabId}>
                    {(props: { selectedTabId: TabType }) => {
                        return this.getTabContent(props.selectedTabId);
                    }}
                </Observer>
            </div>
        );
    }

    private onSelectedTabChanged = (newTabId: TabType) => {
        this.selectedTabId.value = newTabId;
        this.allTabFilter.reset();
        this.changedTabFilter.reset();

        publish(
            this.context.pageContext,
            newTabId === TabType.All ? TelemetryEvents.CodeCoverageCiAllTabClicked : TelemetryEvents.CodeCoverageCiChangedTabClicked,
            this.props.runViewArtifact.buildId
        );
    };

    private onRenderFilterBar = (): JSX.Element => {
        return (
            <Observer selectedTabId={this.selectedTabId}>
                {(props: { selectedTabId: TabType }) => {
                    if (props.selectedTabId === TabType.Changed) {
                        return isFeatureFlagEnabled(this.context.pageContext, "WebAccess.TestManagement.CodeCoverage.ChangedTabFiltering", false) ? (
                            <div className={"padding-right-8"}>
                                <InlineKeywordFilterBarItem
                                    key={"changes-tab-filter-key"}
                                    filter={this.changedTabFilter}
                                    filterItemKey="changes-tab-filter"
                                    placeholder={Resources.FilePathFilterPlaceholder}
                                />
                            </div>
                        ) : null;
                    } else {
                        return isFeatureFlagEnabled(this.context.pageContext, "WebAccess.TestManagement.CodeCoverage.AllTabFiltering", false) ? (
                            <div className={"padding-right-8"}>
                                <InlineKeywordFilterBarItem
                                    key={"all-tab-filter-key"}
                                    filter={this.allTabFilter}
                                    filterItemKey="all-tab-filter"
                                    placeholder={Resources.FilePathFilterPlaceholder}
                                />
                            </div>
                        ) : null;
                    }
                }}
            </Observer>
        );
    };

    private getTabContent(selectedTabId: TabType): JSX.Element {
        if (selectedTabId === TabType.All) {
            return (
                <WrappedComponent
                    dependencies={["ms.vss-codecoverage-ci-web.all-tab-content", "ms.vss-codecoverage-ci-web.code-coverage-ci-alltab-data-provider"]}
                    wrappedType="CodeCoverage.CI.AllTab"
                    runViewArtifact={this.props.runViewArtifact}
                    filter={this.allTabFilter} 
                />
            );
        } else {
            /* Default Tab, even in case we get invalid TabType */
            return (
                <CodeCoverageCiChangedTab
                    summaryResponse={this.props.summaryResponse}
                    runViewArtifact={this.props.runViewArtifact}
                    filter={this.changedTabFilter}
                />
            );
        }
    }
}
